import { useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight, Camera, Download, ImagePlus, Save, Trash2, X } from 'lucide-react'

import { apiRequest } from './api/client'
import { Attachment, AttachmentAction, AttachmentActions, AttachmentContent, AttachmentDescription, AttachmentGroup, AttachmentMedia, AttachmentTitle, AttachmentTrigger } from './components/ui/Attachment'
import { Textarea } from './components/ui/Textarea'

export const EVIDENCE_STAGE_LABELS = {
  reception: 'Recepción del vehículo',
  diagnosis: 'Diagnóstico',
  in_progress: 'En proceso',
  delivery: 'Entrega del vehículo',
}

export async function fileDigest(file) {
  const bytes = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return [...new Uint8Array(bytes)].map((value) => value.toString(16).padStart(2, '0')).join('')
}

export function OrderEvidenceUploader({ order, stage, sourceAction, onUploaded, disabled = false, compact = false }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [urls, setUrls] = useState({})
  const [selectedId, setSelectedId] = useState('')
  const [reference, setReference] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const items = (order.evidence || [])
    .filter((item) => item.stage === stage && item.status === 'ready')
    .sort((left, right) => Number(left.display_order || 0) - Number(right.display_order || 0) || String(left.created_at).localeCompare(String(right.created_at)))
  const selected = items.find((item) => item.id === selectedId) || null
  const itemIds = items.map((item) => item.id).join(',')
  useEffect(() => {
    let active = true
    Promise.all(items.map(async (item) => {
      try { const result = await apiRequest(`/platform/work-orders/${order.id}/evidence/${item.id}/access/`); return [item.id, result.url] } catch { return [item.id, ''] }
    })).then((entries) => { if (active) setUrls(Object.fromEntries(entries)) })
    return () => { active = false }
  }, [order.id, itemIds])
  useEffect(() => {
    setReference(selected?.reference || '')
    setConfirmDelete(false)
  }, [selected?.id, selected?.reference])
  const upload = async (event) => {
    const files = [...(event.target.files || [])]
    if (!files.length) return
    setBusy(true); setError(null)
    try {
      for (const file of files) {
        const checksum = await fileDigest(file)
        const request = await apiRequest(`/platform/work-orders/${order.id}/evidence/upload-request/`, {
          method: 'POST',
          body: { filename: file.name, content_type: file.type, size_bytes: file.size, checksum, classification: 'other', stage, source_action: sourceAction },
        })
        const put = await fetch(request.upload_url, { method: 'PUT', headers: { 'Content-Type': file.type, 'x-amz-meta-checksum': checksum }, body: file })
        if (!put.ok) throw new TypeError('La imagen no llegó al almacenamiento privado. Inténtalo nuevamente.')
        await apiRequest(`/platform/work-orders/${order.id}/evidence/${request.evidence.id}/complete/`, { method: 'POST', body: {} })
      }
      await onUploaded?.()
    } catch (requestError) { setError(requestError) } finally { setBusy(false); event.target.value = '' }
  }
  const saveReference = async () => {
    setBusy(true); setError(null)
    try {
      await apiRequest(`/platform/work-orders/${order.id}/evidence-item/${selected.id}/`, { method: 'PATCH', body: { reference } })
      await onUploaded?.()
      setSelectedId('')
    } catch (requestError) { setError(requestError) } finally { setBusy(false) }
  }
  const move = async (direction) => {
    const index = items.findIndex((item) => item.id === selected.id)
    const destination = index + direction
    if (destination < 0 || destination >= items.length) return
    const ordered = [...items]
    ;[ordered[index], ordered[destination]] = [ordered[destination], ordered[index]]
    setBusy(true); setError(null)
    try {
      await apiRequest(`/platform/work-orders/${order.id}/evidence-order/`, { method: 'POST', body: { stage, ordered_ids: ordered.map((item) => item.id) } })
      await onUploaded?.()
    } catch (requestError) { setError(requestError) } finally { setBusy(false) }
  }
  const remove = async () => {
    setBusy(true); setError(null)
    try {
      await apiRequest(`/platform/work-orders/${order.id}/evidence-item/${selected.id}/`, { method: 'DELETE' })
      setSelectedId('')
      await onUploaded?.()
    } catch (requestError) { setError(requestError) } finally { setBusy(false) }
  }
  return <div className={`order-evidence-upload ${compact ? 'compact' : ''}`}>
    {items.length > 0 && <AttachmentGroup aria-label={`Evidencia de ${EVIDENCE_STAGE_LABELS[stage]}`}>
      {items.map((item) => <Attachment key={item.id} orientation="vertical">
        <AttachmentMedia variant="image">{urls[item.id] ? <img src={urls[item.id]} alt={item.original_filename}/> : <Camera/>}</AttachmentMedia>
        <AttachmentContent><AttachmentTitle>{item.original_filename}</AttachmentTitle><AttachmentDescription>{item.reference || item.source_action || EVIDENCE_STAGE_LABELS[stage]}</AttachmentDescription></AttachmentContent>
        <AttachmentActions><AttachmentAction disabled={!urls[item.id]} onClick={() => window.open(urls[item.id], '_blank', 'noopener,noreferrer')} aria-label={`Abrir ${item.original_filename}`}><Download/></AttachmentAction></AttachmentActions>
        <AttachmentTrigger disabled={disabled && !urls[item.id]} onClick={() => disabled ? window.open(urls[item.id], '_blank', 'noopener,noreferrer') : setSelectedId(item.id)} aria-label={disabled ? `Ver ${item.original_filename}` : `Editar ${item.original_filename}`}/>
      </Attachment>)}
    </AttachmentGroup>}
    <label className="secondary-button">
      <ImagePlus/> {busy ? 'Verificando…' : `Agregar evidencia · ${EVIDENCE_STAGE_LABELS[stage]}`}
      <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple disabled={disabled || busy} onChange={upload}/>
    </label>
    {error && <small className="platform-field-error">{error.message}</small>}
    {selected && <div className="focused-form-layer evidence-editor-layer" role="presentation">
      <button className="focused-form-backdrop" type="button" onClick={() => setSelectedId('')} aria-label="Cerrar editor de fotografía"/>
      <section className="focused-form-dialog evidence-editor-dialog" role="dialog" aria-modal="true" aria-labelledby="evidence-editor-title">
        <header><div><span>FOTOGRAFÍA {items.findIndex((item) => item.id === selected.id) + 1} DE {items.length}</span><h2 id="evidence-editor-title">Editar evidencia</h2><p>{selected.original_filename}</p></div><button type="button" onClick={() => setSelectedId('')} aria-label="Cerrar"><X/></button></header>
        <div className="focused-form-body">
          <div className="evidence-editor-preview">{urls[selected.id] ? <img src={urls[selected.id]} alt={reference || selected.original_filename}/> : <Camera/>}</div>
          <label className="evidence-reference-field">Referencia o leyenda <small>Opcional</small><Textarea value={reference} maxLength={300} onChange={(event) => setReference(event.target.value)} placeholder="Ej. Desgaste irregular en la pastilla de freno delantera derecha"/><span>{reference.length}/300</span></label>
          <section className="evidence-order-controls"><div><strong>Orden de visualización</strong><small>Cambia la posición de esta fotografía dentro de la galería.</small></div><button className="secondary-button" type="button" disabled={busy || items[0]?.id === selected.id} onClick={() => move(-1)}><ArrowLeft/> Mover antes</button><button className="secondary-button" type="button" disabled={busy || items.at(-1)?.id === selected.id} onClick={() => move(1)}>Mover después <ArrowRight/></button></section>
          {confirmDelete ? <section className="evidence-delete-confirm"><div><strong>¿Eliminar esta fotografía?</strong><small>La imagen dejará de mostrarse, pero el movimiento quedará registrado en el historial de la orden.</small></div><button type="button" disabled={busy} onClick={() => setConfirmDelete(false)}>Conservar</button><button type="button" disabled={busy} onClick={remove}><Trash2/> Confirmar eliminación</button></section> : <button className="evidence-delete-button" type="button" disabled={busy} onClick={() => setConfirmDelete(true)}><Trash2/> Eliminar fotografía</button>}
          {error && <p className="form-error">{error.message}</p>}
        </div>
        <footer><button className="secondary-button" type="button" onClick={() => setSelectedId('')}>Cancelar</button><button className="admin-primary" type="button" disabled={busy} onClick={saveReference}><Save/> {busy ? 'Guardando…' : 'Guardar cambios'}</button></footer>
      </section>
    </div>}
  </div>
}

export function OrderEvidenceGallery({ order, items = [], portal = false }) {
  const ready = items.filter((item) => item.status === 'ready').sort((left, right) => Number(left.display_order || 0) - Number(right.display_order || 0) || String(left.created_at).localeCompare(String(right.created_at)))
  const open = async (item) => {
    const result = await apiRequest(portal ? `/portal/evidence/${item.id}/` : `/platform/work-orders/${order.id}/evidence/${item.id}/access/`)
    window.open(result.url, '_blank', 'noopener,noreferrer')
  }
  return <div className="order-evidence-groups">
    {Object.entries(EVIDENCE_STAGE_LABELS).map(([stage, label]) => {
      const stageItems = ready.filter((item) => item.stage === stage)
      if (!stageItems.length) return null
      return <section key={stage} className="order-evidence-stage"><header><Camera/><strong>{label}</strong><span>{stageItems.length}</span></header><div className="evidence-list">{stageItems.map((item) => <button type="button" key={item.id} onClick={() => open(item)}><Camera/><span>{item.original_filename}<small>{item.reference || item.source_action || label}</small></span><Download/></button>)}</div></section>
    })}
    {!ready.length && <p className="platform-empty">No hay evidencia vigente registrada.</p>}
  </div>
}
